import { AccessorErrorManager } from "./AccessorErrorManager.js";
import { Config } from "./Config.js";
import { DirtinessTracker } from "./DirtinessTracker.js";
import { EventManager } from "./EventManager.js";
import { MarkerMerger } from "./MarkerMerger.js";
import { MarkerTreeGenerator } from "./MarkerTreeGenerator.js";
import { MutationGenerator } from "./MutationGenerator.js";
import { QueryGenerator } from "./QueryGenerator.js";
import { StateInitializer } from "./StateInitializer.js";
import { TreeAugmenter } from "./TreeAugmenter.js";
import { TreeFilterGenerator } from "./TreeFilterGenerator.js";
import { TreeRootAccessor } from "../accessors/TreeRootAccessor.js";
import { createBatchUpdatesOptions } from "./factories/createBatchUpdatesOptions.js";
import { getCombinedSignal } from "./utils/getCombinedSignal.js";
import { metadataToRequestError } from "../accessorTree/metadataToRequestError.js";
import { StateIterator } from "./state/StateIterator.js";
import { assertNever } from "../utils/assertNever.js";
import { generateEnumerabilityPreventingEntropy } from "../utils/generateEnumerabilityPreventingEntropy.js";
var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const _DataBinding = class {
  constructor(contentApiClient, systemApiClient, tenantApiClient, treeStore, environment, onUpdate, onError, onPersistSuccess, options) {
    this.contentApiClient = contentApiClient;
    this.systemApiClient = systemApiClient;
    this.tenantApiClient = tenantApiClient;
    this.treeStore = treeStore;
    this.environment = environment;
    this.onUpdate = onUpdate;
    this.onError = onError;
    this.onPersistSuccess = onPersistSuccess;
    this.options = options;
    __publicField(this, "accessorErrorManager");
    __publicField(this, "batchUpdatesOptions");
    __publicField(this, "asyncBatchUpdatesOptions");
    __publicField(this, "bindingOperations");
    __publicField(this, "config");
    __publicField(this, "dirtinessTracker");
    __publicField(this, "eventManager");
    __publicField(this, "stateInitializer");
    __publicField(this, "treeAugmenter");
    __publicField(this, "resolvedOnUpdate", ({ isMutating }) => {
      this.onUpdate(new TreeRootAccessor(this.dirtinessTracker.hasChanges(), isMutating, this.bindingOperations), this);
    });
    __publicField(this, "pendingExtensions", /* @__PURE__ */ new Set());
    this.config = new Config();
    this.batchUpdatesOptions = createBatchUpdatesOptions(environment, this.treeStore);
    this.asyncBatchUpdatesOptions = {
      ...this.batchUpdatesOptions,
      contentClient: contentApiClient,
      systemClient: systemApiClient,
      tenantClient: tenantApiClient
    };
    this.dirtinessTracker = new DirtinessTracker();
    this.eventManager = new EventManager(
      this.asyncBatchUpdatesOptions,
      this.batchUpdatesOptions,
      this.config,
      this.dirtinessTracker,
      this.resolvedOnUpdate,
      this.treeStore
    );
    this.accessorErrorManager = new AccessorErrorManager(this.eventManager, this.treeStore);
    this.stateInitializer = new StateInitializer(
      this.accessorErrorManager,
      this.batchUpdatesOptions,
      this.eventManager,
      this.treeStore
    );
    this.treeAugmenter = new TreeAugmenter(this.eventManager, this.stateInitializer, this.treeStore, options.skipStateUpdateAfterPersist);
    this.bindingOperations = Object.freeze({
      ...this.asyncBatchUpdatesOptions,
      getTreeFilters: () => {
        const generator = new TreeFilterGenerator(this.treeStore);
        return generator.generateTreeFilter();
      },
      batchDeferredUpdates: (performUpdates) => {
        this.eventManager.syncTransaction(() => performUpdates(this.bindingOperations));
      },
      extendTree: async (...args) => await this.extendTree(...args),
      persist: async (options2) => await this.persist(options2)
    });
  }
  async persist({ onPersistError, onPersistSuccess, signal } = {}) {
    return await this.eventManager.persistOperation(async () => {
      this.eventManager.syncTransaction(() => {
        this.accessorErrorManager.clearErrors();
      });
      await this.eventManager.triggerOnBeforePersist();
      await this.checkErrorsBeforePersist(onPersistError);
      const generator = new MutationGenerator(this.treeStore);
      const mutationResult = generator.getPersistMutation();
      if (mutationResult === void 0) {
        await this.processEmptyPersistMutation(onPersistSuccess);
        return {
          type: "nothingToPersist"
        };
      }
      const { query, operations } = mutationResult;
      const mutationResponse = await this.contentApiClient.sendRequest(query, {
        signal
      });
      if (mutationResponse.errors !== void 0 && mutationResponse.errors.length > 0) {
        this.onError({
          type: "gqlError",
          query,
          errors: mutationResponse.errors
        }, this);
      }
      if (!mutationResponse.data?.transaction) {
        this.persistFail({
          errors: [],
          type: "invalidResponse"
        });
      }
      const { __typename, ok, errorMessage, ...mutationData } = mutationResponse.data.transaction;
      if (Object.values(mutationData).every((it) => it.ok)) {
        return await this.processSuccessfulPersistResult(mutationData, operations, onPersistSuccess);
      } else {
        if (errorMessage) {
          console.error(errorMessage);
        }
        this.eventManager.syncTransaction(() => this.accessorErrorManager.replaceErrors(mutationData, operations));
        await this.eventManager.triggerOnPersistError(this.bindingOperations);
        await onPersistError?.(this.bindingOperations);
        this.persistFail({
          errors: this.accessorErrorManager.getErrors(),
          type: "invalidInput"
        });
      }
    });
  }
  async checkErrorsBeforePersist(onPersistError) {
    if (this.accessorErrorManager.hasErrors()) {
      await this.eventManager.triggerOnPersistError(this.bindingOperations);
      await onPersistError?.(this.bindingOperations);
      this.persistFail(
        {
          errors: this.accessorErrorManager.getErrors(),
          type: "invalidInput"
        }
      );
    }
  }
  async processEmptyPersistMutation(onPersistSuccess) {
    this.dirtinessTracker.reset();
    const persistSuccessOptions = {
      ...this.bindingOperations,
      successType: "nothingToPersist"
    };
    await this.eventManager.triggerOnPersistSuccess(persistSuccessOptions);
    await onPersistSuccess?.(persistSuccessOptions);
  }
  async processSuccessfulPersistResult(mutationData, operations, onPersistSuccess) {
    const persistedEntityIds = Object.values(mutationData).map((it) => it.node?.id).filter((id) => id !== void 0);
    const result = {
      type: "justSuccess",
      persistedEntityIds
    };
    try {
      await this.eventManager.asyncTransaction(async () => {
        this.resetTreeAfterSuccessfulPersist();
        this.treeAugmenter.updatePersistedData(
          Object.fromEntries(
            Object.entries(mutationData).map(([placeholderName, subTreeResponse]) => [
              placeholderName,
              subTreeResponse.node
            ])
          ),
          operations
        );
        const persistSuccessOptions = {
          ...this.bindingOperations,
          successType: result.type
        };
        await this.eventManager.triggerOnPersistSuccess(persistSuccessOptions);
        await onPersistSuccess?.(persistSuccessOptions);
        this.treeAugmenter.resetCreatingSubTrees();
      });
    } catch (e) {
      console.error(e);
      const resultWithError = {
        ...result,
        afterPersistError: e
      };
      this.onPersistSuccess(resultWithError, this);
      return resultWithError;
    }
    this.onPersistSuccess(result, this);
    return result;
  }
  persistFail(error) {
    throw error;
  }
  async extendTree(newFragment, options = {}) {
    if (options.signal?.aborted) {
      return Promise.reject(DataBindingExtendAborted);
    }
    const markerTreeRoot = new MarkerTreeGenerator(newFragment, options.environment ?? this.environment).generate();
    if (this.treeStore.effectivelyHasTreeRoot(markerTreeRoot)) {
      return this.eventManager.syncOperation(() => {
        const newTreeRootId = this.getNewTreeRootId();
        this.treeAugmenter.extendTreeStates(newTreeRootId, markerTreeRoot);
        return newTreeRootId;
      });
    }
    return await new Promise((resolve, reject) => {
      this.pendingExtensions.add({
        markerTreeRoot,
        newFragment,
        newTreeRootId: this.getNewTreeRootId(),
        options,
        reject: () => reject(DataBindingExtendAborted),
        resolve
      });
      const pendingExtensionsCount = this.pendingExtensions.size;
      Promise.resolve().then(() => {
        if (pendingExtensionsCount < this.pendingExtensions.size) {
          return;
        }
        this.flushBatchedTreeExtensions();
      });
    });
  }
  async flushBatchedTreeExtensions() {
    const pendingExtensions = Array.from(this.pendingExtensions).filter((extension) => {
      if (extension.options.signal?.aborted) {
        extension.reject();
        return false;
      }
      return true;
    });
    this.pendingExtensions.clear();
    const aggregateMarkerTreeRoot = pendingExtensions.reduce(
      (previousValue, currentValue) => {
        if (previousValue === void 0) {
          return currentValue.markerTreeRoot;
        }
        return MarkerMerger.mergeMarkerTreeRoots(previousValue, currentValue.markerTreeRoot);
      },
      void 0
    );
    if (aggregateMarkerTreeRoot === void 0) {
      return;
    }
    const aggregateSignal = getCombinedSignal(pendingExtensions.map((extension) => extension.options.signal));
    const aggregatePersistedData = await this.fetchPersistedData(aggregateMarkerTreeRoot, aggregateSignal);
    if (aggregateSignal?.aborted || aggregatePersistedData === void 0) {
      return;
    }
    this.eventManager.syncOperation(() => {
      this.treeAugmenter.extendPersistedData(aggregatePersistedData.data, aggregateMarkerTreeRoot);
      for (const extension of pendingExtensions) {
        this.treeAugmenter.extendTreeStates(extension.newTreeRootId, extension.markerTreeRoot);
        extension.resolve(extension.newTreeRootId);
      }
    });
  }
  async fetchPersistedData(tree, signal) {
    const queryGenerator = new QueryGenerator(tree);
    const query = queryGenerator.getReadQuery();
    let queryResponse = void 0;
    try {
      queryResponse = query === void 0 ? void 0 : await this.contentApiClient.sendRequest(query, {
        signal
      });
    } catch (metadata) {
      if (typeof metadata === "object" && metadata !== null && metadata.name === "AbortError") {
        return;
      }
      this.onError(metadataToRequestError(metadata), this);
    }
    if (queryResponse && queryResponse.errors !== void 0 && queryResponse.errors.length > 0) {
      this.onError({
        type: "gqlError",
        query: query ?? "unknown query",
        errors: queryResponse.errors
      }, this);
    }
    return queryResponse;
  }
  resetTreeAfterSuccessfulPersist() {
    this.eventManager.syncTransaction(() => {
      this.accessorErrorManager.clearErrors();
      this.dirtinessTracker.reset();
      for (const [, rootState] of StateIterator.eachRootState(this.treeStore)) {
        for (const state of StateIterator.depthFirstAllNodes(rootState)) {
          switch (state.type) {
            case "field": {
              if (state.hasUnpersistedChanges || state.touchLog?.size) {
                state.touchLog?.clear();
                state.hasUnpersistedChanges = false;
                this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
              }
              break;
            }
            case "entityList":
              if (state.unpersistedChangesCount > 0 || state.plannedRemovals?.size) {
                state.unpersistedChangesCount = 0;
                state.plannedRemovals?.clear();
                this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
              }
              break;
            case "entityRealm": {
              if (state.unpersistedChangesCount > 0 || state.plannedHasOneDeletions?.size) {
                state.unpersistedChangesCount = 0;
                state.plannedHasOneDeletions?.clear();
                this.eventManager.registerJustUpdated(state, EventManager.NO_CHANGES_DIFFERENCE);
              }
              break;
            }
            default:
              assertNever();
          }
        }
      }
    });
  }
  getNewTreeRootId() {
    return this.treeStore.markerTrees.size === 0 ? void 0 : `${generateEnumerabilityPreventingEntropy()}-${_DataBinding.getNextTreeRootIdSeed()}`;
  }
};
let DataBinding = _DataBinding;
__publicField(DataBinding, "getNextTreeRootIdSeed", (() => {
  let seed = 1;
  return () => seed++;
})());
const DataBindingExtendAborted = Symbol("aborted");
export {
  DataBinding,
  DataBindingExtendAborted
};
//# sourceMappingURL=DataBinding.js.map
